/**
 * Suspended — /suspended
 * Account suspended screen. Shows reason (if available) and sign-out button.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { ShieldOff, LogOut, Mail, ArrowRightLeft } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';

const SuspendedPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [reason, setReason] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [suspendedDate, setSuspendedDate] = useState<string | null>(null);

  useEffect(() => {
    // Try to read suspension info from query params or session
    const reasonParam = params.get('reason');
    const wsParam = params.get('workspace');
    const dateParam = params.get('date');

    if (reasonParam) setReason(decodeURIComponent(reasonParam));
    if (wsParam) setWorkspaceName(decodeURIComponent(wsParam));
    if (dateParam) setSuspendedDate(dateParam);
  }, [params]);

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  }

  function handleContactAdmin() {
    // Open mailto for support
    window.location.href = 'mailto:soporte@theplatform360.com?subject=Cuenta%20suspendida';
  }

  function handleSwitchWorkspace() {
    navigate('/pending-invites');
  }

  const formattedDate = suspendedDate
    ? (() => {
        try {
          return new Date(suspendedDate).toLocaleDateString('es-DO', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          });
        } catch {
          return suspendedDate;
        }
      })()
    : null;

  return (
    <AuthLayout>
      <div className="w-full flex flex-col gap-5">
        {/* User header with sign out */}
        <div className="flex items-center justify-end">
          <button
            onClick={handleSignOut}
            className="text-xs text-primary hover:underline font-medium"
          >
            Salir
          </button>
        </div>

        {/* Icon + Title */}
        <div className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center">
            <ShieldOff className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">
            Tu acceso esta suspendido.
          </h1>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            {workspaceName ? (
              <>
                El admin de <strong className="text-foreground">{workspaceName}</strong> suspendio tu acceso a este workspace
                {formattedDate && (
                  <> el <strong className="text-foreground">{formattedDate}</strong></>
                )}
                .
              </>
            ) : (
              'Tu acceso a este workspace ha sido suspendido por un administrador.'
            )}
          </p>
        </div>

        {/* Reason card */}
        <div className="rounded-lg border border-border bg-muted/50 p-4">
          <div className="font-mono text-[10px] tracking-wider uppercase text-muted-foreground font-semibold mb-2">
            Motivo
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {reason || 'Las suspensiones son revisables por el admin. Tus datos no se eliminan y puedes ser reactivado en cualquier momento.'}
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <Button
            variant="outline"
            className="w-full"
            onClick={handleContactAdmin}
          >
            <Mail className="w-4 h-4 mr-2" />
            Escribir al admin
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={handleSwitchWorkspace}
          >
            <ArrowRightLeft className="w-4 h-4 mr-2" />
            Cambiar de workspace
          </Button>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Si crees que es un error,{' '}
          <a
            href="mailto:soporte@theplatform360.com"
            className="text-primary hover:underline font-medium"
          >
            reportar al soporte
          </a>
          .
        </p>
      </div>
    </AuthLayout>
  );
};

export default SuspendedPage;
